import { useState } from 'react';
import { useQuery, useMutation, useIsMutating } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faX,
  faCheckCircle,
  faXmarkCircle,
  faEye,
  faFlag,
} from '@fortawesome/free-solid-svg-icons';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import JSONPretty from 'react-json-pretty';
import { withAppHOC } from '../../hoc';
import {
  StyledContentContainer,
  StyledActionItemButtons,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledStatusPill,
  StyledModalContainer,
  StyledFormContainer,
  StyledIconedText
} from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import {
  MessageBox,
  Dropdown,
  TextInput,
  CustomConfirmButton,
  CustomButton,
  Form,
  Fieldset,
  Label,
  Button,
} from '../../components/FormElements';
import Modal from '../../components/Modal';
import Accordination from '../../components/Accordination/Accordination';
import { Grid, GridItem } from '../../components/Grid';
import Loader from '../../components/Loader/Loader';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../components/NoData/NoData';
import { TableBuilder } from '../../components/TableElements';
import Pagination from '../../components/Pagination/Pagination';
import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import { formatCurrency, getSearchParams } from '../../utils';
import FiltersButton from '../../components/FiltersButton/FiltersButton';
import { Image } from '../../components/Image';

const StyledBodyContainer = styled.section`
  font-size: 14px;
  font-weight: normal;
  color: #000;
  word-break: break-all;
  padding: 0px;
  line-height: 20px;
  border: 1px solid #e5e5e5;
  margin: 20px;
  border-radius: 5px;
`;

const StyledHeading = styled.h2`
  font-size: 16px;
  font-weight: normal;
  color: #000;
  background-color: #e5e5e5;
  padding: 15px;
  margin: 0px;
`;

const StyledContent = styled.section`
  padding: 20px;
  @media (max-width: 600px) {
    word-break: break-all;
    overflow-x: scroll;
  }
`;

const StyledApiCallLogsContainer = styled.section`
  width: auto;
  height: auto;
  max-height: 500px;
  overflow-y: scroll;
  padding: 20px;
  pre {
    white-space: pre-wrap;
  }
`;

const SellerTransactionsComplaintsList = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    fOperatorsId: '',
    fCirclesId: '',
    fCategoriesId: '',
    fComplaintResolutionStatus: 'Pending',
    fRechargeNumber: '',
    fFromDate: '',
    fToDate: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [isLogsLoading, setIsLogsLoading] = useState(false);
  const [modalType, setModalType] = useState('');
  const [logs, setLogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [sellersComplaintStatus, setSellersComplaintStatus] = useState("");
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 30,
  });
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [sellersOperatorsId, setSellersOperatorsId] = useState("");
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [selectedComplaintId, setSelectedComplaintId] = useState(null);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateTransaction = async ({
    id,
    complaintStatus,
    rechargeOperatorsId,
    complaintSellerRemark,
  }) => {
    try {
      const response = await API.post(`/seller/transactions/complaint`, {
        id,
        complaintStatus,
        rechargeOperatorsId,
        complaintSellerRemark,
      });
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const readData = async () => {
    try {
      const response = await API.get(
        `/seller/transactions?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fOperatorsId=${appliedFilters.fOperatorsId}&fCirclesId=${appliedFilters.fCirclesId}&fCategoriesId=${appliedFilters.fCategoriesId}&fRechargeNumber=${appliedFilters.fRechargeNumber}&fFromDate=${appliedFilters.fFromDate}&fToDate=${appliedFilters.fToDate}&fComplaintResolutionStatus=${appliedFilters.fComplaintResolutionStatus}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const readApiLogs = async (id) => {
    setIsLogsLoading(true);
    try {
      const response = await API.get(`/seller/transactions/logs/${id}`);
      if (response.data.status === RESPONSE_STATUSES.SUCCESS) {
        setLogs(response?.data?.data);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      setIsLogsLoading(false);
    }
  };

  const { mutateAsync: updateTransactionMutation } = useMutation({
    mutationFn: updateTransaction,
    onError: (error) => console.error(error),
    onSuccess: () => {
      reloadData();
    },
  });

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['complaints_transactions', paginationData, appliedFilters],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: meta } = useQuery({
    queryKey: ['meta'],
    queryFn: readMeta,
  });

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      fOperatorsId: '',
      fCirclesId: '',
      fCategoriesId: '',
      fComplaintResolutionStatus: 'Pending',
      fRechargeNumber: '',
      fFromDate: '',
      fToDate: '',
    });
    setAppliedFilters({
      fOperatorsId: '',
      fCirclesId: '',
      fCategoriesId: '',
      fComplaintResolutionStatus: 'Pending',
      fRechargeNumber: '',
      fFromDate: '',
      fToDate: '',
    });
  };

  const isMutation = useIsMutating();

  const renderPageHeading = () => (
    <PageHeader
      title="Complaint Transactions"
      showAddButton={false}
      showReloadData={true}
      reloadData={reloadData}
    />
  );

  const operatorsOptions = [
    {
      name: 'OPERATOR',
      value: '',
    },
  ];
  !isEmpty(meta?.operators) &&
    meta?.operators?.forEach(({ operatorName, id }) =>
      operatorsOptions?.push({ name: operatorName, value: id })
    );

  const circlesOptions = [
    {
      name: 'CIRCLE',
      value: '',
    },
  ];
  !isEmpty(meta?.circles) &&
    meta?.circles?.forEach(({ circleName, id }) =>
      circlesOptions?.push({ name: circleName, value: id })
    );

  const categoriesOptions = [
    {
      name: 'CATEGORY',
      value: '',
    },
  ];
  !isEmpty(meta?.categories) &&
    meta?.categories?.forEach(({ category, id }) =>
      categoriesOptions?.push({ name: category, value: id })
    );

  const resolutionStatusOptions = [
    {
      name: 'RESOLUTION STATUS',
      value: '',
    },
    {
      name: 'All',
      value: 'All',
    },
    {
      name: 'Pending',
      value: 'Pending',
    },
    {
      name: 'Resolved',
      value: 'Resolved',
    },
  ];

  const toggleApiLogs = async (id) => {
    setModalType('API_LOGS');
    setShowModal(true);
    await readApiLogs(id);
  };

  const handleComplaintUpdate = async (e) => {
    e.preventDefault();

    if (isEmpty(sellersComplaintStatus)) {
      setResponseStatus(RESPONSE_STATUSES.FAILED);
      setResponseMessage('Please select complaint action');
      return;
    }

    if (
      sellersComplaintStatus === 'Rejected' &&
      isEmpty(sellersOperatorsId)
    ) {
      setResponseStatus(RESPONSE_STATUSES.FAILED);
      setResponseMessage('Please enter operators id');
      return;
    }

    try {
      setIsLoadingUpdate(true);
      setResponseStatus('');
      setResponseMessage('');

      await updateTransactionMutation({
        id: selectedComplaintId,
        complaintStatus: sellersComplaintStatus,
        rechargeOperatorsId: sellersOperatorsId,
      });

      setShowModal(false);
      setModalType('');
      setSellersComplaintStatus('');
      setSellersOperatorsId('');
      setSelectedComplaintId(null);
    } catch (error) {
      setResponseStatus(RESPONSE_STATUSES.FAILED);
      setResponseMessage(error.message);
    } finally {
      setIsLoadingUpdate(false);
    }
  };

  const toggleComplaintAction = (id) => {
    setSelectedComplaintId(id);
    setSellersComplaintStatus('');
    setSellersOperatorsId('');
    setResponseStatus('');
    setResponseMessage('');
    setModalType('UPDATE');
    setShowModal(true);
  };

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isMutation > 0 || isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        <StyledFiltersContainer className={showFilters ? 'show' : ''}>
          <StyledFilters>
            <StyledFilterContainer>
              <Dropdown
                value={filters.fOperatorsId}
                onChange={(value) => handleFilterChange('fOperatorsId', value)}
                placeholder="Operator"
                disabled={isLoading}
                options={operatorsOptions}
              />
              <Dropdown
                value={filters.fCirclesId}
                onChange={(value) => handleFilterChange('fCirclesId', value)}
                placeholder="Circle"
                disabled={isLoading}
                options={circlesOptions}
              />
              <Dropdown
                value={filters.fCategoriesId}
                onChange={(value) => handleFilterChange('fCategoriesId', value)}
                placeholder="Category"
                disabled={isLoading}
                options={categoriesOptions}
              />
              <Dropdown
                value={filters.fComplaintResolutionStatus}
                onChange={(value) =>
                  handleFilterChange('fComplaintResolutionStatus', value)
                }
                placeholder="Resolution Status"
                disabled={isLoading}
                options={resolutionStatusOptions}
              />
              <TextInput
                value={filters.fRechargeNumber}
                onChange={(value) =>
                  handleFilterChange('fRechargeNumber', value)
                }
                placeholder="Recharge Number"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fFromDate}
                onChange={(value) => handleFilterChange('fFromDate', value)}
                placeholder="From Date"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fToDate}
                onChange={(value) => handleFilterChange('fToDate', value)}
                placeholder="To Date"
                disabled={isLoading}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: 'API Name',
                  dataSelector: 'apiName',
                  dataType: 'string',
                  cellrenderer: (value) => value || 'N/A',
                },
                {
                  title: 'Operator',
                  dataSelector: 'operatorName',
                  dataType: 'string',
                  cellrenderer: (value, item) => (
                    <StyledIconedText>
                      <Image
                        source={item.operatorsLogo || '/no-image.png'}
                        alt={value}
                        style={{ width: '50px', height: '50px' }}
                      />
                      {value}
                    </StyledIconedText>
                  ),
                },
                {
                  title: 'Recharge Number',
                  dataSelector: 'rechargeNumber',
                  dataType: 'string',
                },
                {
                  title: 'Amount',
                  dataSelector: 'rechargeAmount',
                  dataType: 'number',
                  cellrenderer: (value) => formatCurrency(value),
                },
                {
                  title: 'Transaction Status',
                  dataSelector: 'sellersStatus',
                  dataType: 'string',
                  textAlign: 'center',
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={
                        value === 'Success'
                          ? 'green'
                          : value === 'Failed'
                            ? 'red'
                            : 'yellow'
                      }
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: 'Complaint Status',
                  dataSelector: 'complaintResolutionStatus',
                  dataType: 'string',
                  textAlign: 'center',
                  cellrenderer: (value, item) => (
                    <StyledStatusPill
                      className={
                        value === 'Resolved' && item.sellersStatus === 'Success'
                          ? 'red'
                          : value === 'Resolved' &&
                            item.sellersStatus === 'Failed'
                            ? 'green'
                            : item.complaintReopened === 'Yes'
                              ? 'blue'
                              : 'yellow'
                      }
                    >
                      {value === 'Resolved' && item.sellersStatus === 'Success'
                        ? 'Rejected'
                        : value === 'Resolved' &&
                          item.sellersStatus === 'Failed'
                          ? 'Accepted'
                          : value === 'Pending' &&
                            item.complaintReopened === 'Yes'
                            ? 'Reopened'
                            : 'Pending'}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: 'Complaint Raised Time',
                  dataSelector: 'complaintRaisedTimestamp',
                  dataType: 'string',
                  cellrenderer: (value) => (value ? value : 'N/A'),
                },
                {
                  title: 'Complaint Resolution Time',
                  dataSelector: 'complaintResolutionTimestamp',
                  dataType: 'string',
                  cellrenderer: (value) => (value ? value : 'N/A'),
                },
                {
                  title: 'Buyers Remark',
                  dataSelector: 'complaintBuyerRemark',
                  dataType: 'string',
                  cellrenderer: (value) => value || 'N/A',
                },
                {
                  title: 'Operators Id',
                  dataSelector: 'sellersOperatorsId',
                  dataType: 'string',
                  cellrenderer: (value) => value || 'N/A',
                },
                {
                  title: 'API Transactions Id',
                  dataSelector: 'sellersApiTransactionsId',
                  dataType: 'string',
                  cellrenderer: (value) => value || 'N/A',
                },
                {
                  title: 'Transaction Time',
                  dataSelector: 'createdOn',
                  dataType: 'string',
                },
                // {
                //   title: '',
                //   dataSelector: 'id',
                //   canSort: false,
                //   isSticky: 'Yes',
                //   stickyAlign: 'right',
                //   isDummy: 'Yes',
                //   textAlign: 'center',
                //   width: 'max-content',
                //   cellrenderer: (value, item) => (
                //     <StyledActionItemButtons>
                //       <CustomButton
                //         icon={faEye}
                //         onClick={() => toggleApiLogs(value)}
                //         style={{ color: 'orange' }}
                //       />
                //       {item.complaintResolutionStatus === 'Pending' && item.complaintOccurrenceCount < 3 && (
                //         <>
                //           {/* <CustomConfirmButton
                //             showIcon={true}
                //             showTitle={false}
                //             icon={faCheckCircle}
                //             color="green"
                //             hoverColor="#005a00"
                //             title="Approve Complaint"
                //             proceedButtonText="Approve"
                //             message="Are you sure, you want to approve this complaint request ?"
                //             onClick={async () => {
                //               await updateTransactionMutation({
                //                 id: value,
                //                 complaintStatus: 'Approved',
                //               });
                //             }}
                //           />
                //           <CustomConfirmButton
                //             showIcon={true}
                //             showTitle={false}
                //             isSellerUpdateComplaint={true}
                //             icon={faXmarkCircle}
                //             title="Reject Complaint"
                //             proceedButtonText="Reject"
                //             message="Are you sure, you want to reject this complaint request ?"
                //             onClick={async (state) => {
                //               await updateTransactionMutation({
                //                 id: value,
                //                 complaintStatus: 'Rejected',
                //                 ...state,
                //               });
                //             }} */}
                //           icon={faFlag}
                //           onClick={() => toggleComplaintAction(value)}
                //           style={{ color: 'red' }}
                //         </>
                //       )}
                //     </StyledActionItemButtons>
                //   ),
                // },
                {
                  title: '',
                  dataSelector: 'id',
                  canSort: false,
                  isSticky: 'Yes',
                  stickyAlign: 'right',
                  isDummy: 'Yes',
                  textAlign: 'center',
                  width: 'max-content',
                  cellrenderer: (value, item) => (
                    <StyledActionItemButtons>
                      <CustomButton
                        icon={faEye}
                        onClick={() => toggleApiLogs(value)}
                        style={{ color: 'orange' }}
                      />

                      {item.complaintResolutionStatus === 'Pending' &&
                        item.complaintOccurrenceCount < 3 && (
                          <CustomButton
                            icon={faFlag}
                            onClick={() => toggleComplaintAction(value)}
                            style={{ color: 'red' }}
                          />
                        )}
                    </StyledActionItemButtons>
                  ),
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
            <Modal
              modalStatus={showModal}
              setModalStatus={() => {
                setShowModal(false);
                setModalType('');
                setLogs([]);
              }}
              hideCloseButton
            >
              <StyledModalContainer
                width={modalType === 'API_LOGS' ? `1200px` : `500px`}
              >
                <h3>{modalType === 'API_LOGS' ? `API Call Logs` : `Complaint`}</h3>
                {modalType === 'API_LOGS' && (
                  <StyledApiCallLogsContainer>
                    {isLogsLoading && <LoadingSpinner />}
                    {!isLogsLoading && logs.length === 0 && (
                      <NoData message={`No logs found`} />
                    )}
                    {logs?.length > 0 &&
                      logs.map(
                        (
                          {
                            requestType,
                            request,
                            requestOn,
                            requestBody,
                            response,
                            responseOn,
                          },
                          index
                        ) => (
                          <Accordination
                            key={index}
                            header={`${requestType}`}
                            autoExpanded={true}
                          >
                            <Grid columns={'calc(50% - 10px) calc(50% - 10px)'}>
                              <GridItem>
                                <StyledBodyContainer>
                                  <StyledHeading>
                                    API Request{' '}
                                    {requestOn && `( ${requestOn} )`}
                                  </StyledHeading>
                                  <StyledContent>{request}</StyledContent>
                                </StyledBodyContainer>
                                {requestBody && (
                                  <StyledBodyContainer>
                                    <StyledHeading>
                                      API Request Body
                                    </StyledHeading>
                                    <StyledContent>{requestBody}</StyledContent>
                                  </StyledBodyContainer>
                                )}
                              </GridItem>
                              <GridItem>
                                <StyledBodyContainer>
                                  <StyledHeading>
                                    API Response{' '}
                                    {responseOn && `( ${responseOn} )`}
                                  </StyledHeading>
                                  <StyledContent>
                                    <JSONPretty
                                      id="json-pretty"
                                      data={response}
                                    ></JSONPretty>
                                  </StyledContent>
                                </StyledBodyContainer>
                              </GridItem>
                            </Grid>
                          </Accordination>
                        )
                      )}
                  </StyledApiCallLogsContainer>
                )}
                {modalType === "UPDATE" && (
                  <StyledFormContainer>
                    <Form method="POST" action="#" onSubmit={handleComplaintUpdate}>
                      <Fieldset>
                        <Label required>Transaction Status</Label>
                        <Dropdown
                          value={sellersComplaintStatus}
                          onChange={setSellersComplaintStatus}
                          placeholder="Complaint Action"
                          options={[
                            {
                              name: "-- SELECT ACTION --",
                              value: "",
                            },
                            {
                              name: "Transaction Is Successful",
                              value: "Rejected",
                            },
                            {
                              name: "Transaction Is Refunded",
                              value: "Approved",
                            },
                          ]}
                          disabled={isLoadingUpdate}
                        />
                      </Fieldset>
                      {sellersComplaintStatus === "Rejected" && (
                      <Fieldset>
                        <Label required={sellersComplaintStatus === "Rejected"}>
                          Operators ID
                        </Label>
                        <TextInput
                          value={sellersOperatorsId}
                          onChange={setSellersOperatorsId}
                          placeholder="Please enter operators id"
                          disabled={isLoadingUpdate}
                        />
                      </Fieldset>
                      )}
                      {showModal && responseStatus !== "" && (
                        <Fieldset>
                          <MessageBox
                            status={responseStatus}
                            message={responseMessage}
                          />
                        </Fieldset>
                      )}
                      <Fieldset>
                        <Button
                          disabled={isLoadingUpdate}
                          type="submit"
                          style={{ width: "auto" }}
                        >
                          Update
                        </Button>
                      </Fieldset>
                      <Loader isLoading={isLoadingUpdate} />
                    </Form>
                  </StyledFormContainer>
                )}
              </StyledModalContainer>
            </Modal>
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(SellerTransactionsComplaintsList);
